import { apiClient } from './client';
import type {
  ChatMessageModel,
  ChatRoomModel,
  SendMessagePayload,
} from '@/types/chat';

// KSAdmin ChatController est monte a la racine sur "chat" (pas de prefixe /api/).
const CHAT_BASE = '/chat';

export async function fetchRooms(includeArchived = false): Promise<ChatRoomModel[]> {
  const { data } = await apiClient.get<ChatRoomModel[]>(`${CHAT_BASE}/rooms`, {
    params: { includeArchived },
  });
  return data;
}

export async function fetchRoom(roomId: number): Promise<ChatRoomModel> {
  const { data } = await apiClient.get<ChatRoomModel>(`${CHAT_BASE}/rooms/${roomId}`);
  return data;
}

/** Cree (ou recupere) un DM 1:1 avec un autre utilisateur. */
export async function getOrCreateDirectRoom(otherUserId: number): Promise<ChatRoomModel> {
  const { data } = await apiClient.post<ChatRoomModel>(
    `${CHAT_BASE}/rooms/direct/${otherUserId}`,
  );
  return data;
}

/** Recupere les messages d'une room (pagination par beforeId). */
export async function fetchMessages(
  roomId: number,
  beforeId?: number,
  limit = 50,
): Promise<ChatMessageModel[]> {
  const params: Record<string, unknown> = { limit };
  if (beforeId !== undefined) params.beforeId = beforeId;
  const { data } = await apiClient.get<ChatMessageModel[]>(
    `${CHAT_BASE}/rooms/${roomId}/messages`,
    { params },
  );
  return data;
}

export async function sendMessage(payload: SendMessagePayload): Promise<ChatMessageModel> {
  const { data } = await apiClient.post<ChatMessageModel>(
    `${CHAT_BASE}/rooms/${payload.chatRoomId}/messages`,
    {
      content: payload.content,
      messageType: payload.messageType ?? 'Text',
      replyToMessageId: payload.replyToMessageId ?? null,
      fileId: payload.fileId ?? null,
      fileName: payload.fileName ?? null,
    },
  );
  return data;
}

export async function markRoomAsRead(roomId: number): Promise<void> {
  await apiClient.post(`${CHAT_BASE}/rooms/${roomId}/read`);
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<{ unreadCount?: number; count?: number } | number>(
    `${CHAT_BASE}/unread-count`,
  );
  if (typeof data === 'number') return data;
  return data.unreadCount ?? data.count ?? 0;
}

export async function fetchRoomUnreadCount(roomId: number): Promise<number> {
  const { data } = await apiClient.get<{ unreadCount?: number; count?: number } | number>(
    `${CHAT_BASE}/rooms/${roomId}/unread-count`,
  );
  if (typeof data === 'number') return data;
  return data.unreadCount ?? data.count ?? 0;
}

export async function editMessage(messageId: number, content: string): Promise<ChatMessageModel> {
  const { data } = await apiClient.put<ChatMessageModel>(
    `${CHAT_BASE}/messages/${messageId}`,
    { content },
  );
  return data;
}

export async function deleteMessage(messageId: number): Promise<void> {
  await apiClient.delete(`${CHAT_BASE}/messages/${messageId}`);
}

export async function archiveRoom(roomId: number): Promise<void> {
  await apiClient.post(`${CHAT_BASE}/rooms/${roomId}/archive`);
}
